import { Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { LeaveTypeListItem } from "@/modules/hr/leave/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type LeaveEntitlementsSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  year?: number;
  leaveTypeId?: string;
  search?: string;
};

const VALUE_ALL = "__all__";

type LeaveEntitlementsFilterProps = {
  searchParams: LeaveEntitlementsSearchParams;
  fromRoute: "/app/hr/leave/entitlements";
  leaveTypes: LeaveTypeListItem[];
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: 5 },
  (_, i) => currentYear - 2 + i,
).map((y) => ({ value: String(y), label: String(y) }));

export function LeaveEntitlementsFilter({
  searchParams,
  fromRoute,
  leaveTypes,
}: LeaveEntitlementsFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [yearInput, setYearInput] = useState(
    search.year != null ? String(search.year) : String(currentYear),
  );
  const [leaveTypeInput, setLeaveTypeInput] = useState(
    search.leaveTypeId ?? VALUE_ALL,
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setYearInput(
      search.year != null ? String(search.year) : String(currentYear),
    );
    setLeaveTypeInput(search.leaveTypeId ?? VALUE_ALL);
  }, [search.search, search.year, search.leaveTypeId]);

  const doNav = (
    opts: Partial<{ year: string; leaveType: string; searchVal: string }> = {},
  ) => {
    const y = opts.year ?? yearInput;
    const lt = opts.leaveType ?? leaveTypeInput;
    const s = opts.searchVal ?? searchInput;
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        year: Number(y),
        leaveTypeId: lt === VALUE_ALL ? undefined : lt,
        search: s.trim() || undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback(
    (val: string) => doNav({ searchVal: val }),
    400,
  );

  const typeOptions = [
    { value: VALUE_ALL, label: "ທັງໝົດ" },
    ...leaveTypes.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` })),
  ];

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="leave-entitlements-search"
            aria-label="ຄົ້ນຫາ"
            placeholder="ຄົ້ນຫາພະນັກງານ ຫຼື ປະເພດລາ..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <SimpleSelect
          options={YEAR_OPTIONS}
          value={yearInput}
          onValueChange={(v) => {
            setYearInput(v);
            doNav({ year: v });
          }}
          placeholder="ປີ"
          className="h-8 w-full sm:w-24"
          aria-label="ກັ່ນຕອງຕາມປີ"
        />
        <SimpleSelect
          options={typeOptions}
          value={leaveTypeInput}
          onValueChange={(v) => {
            setLeaveTypeInput(v);
            doNav({ leaveType: v });
          }}
          placeholder="ປະເພດລາ"
          className="h-8 w-full sm:w-40"
          aria-label="ກັ່ນຕອງຕາມປະເພດລາ"
        />
      </div>
    </div>
  );
}
